import AsyncCreatableSelect from 'react-select/async-creatable';
import { useCallback, useEffect, useState,} from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '../../API';
import Modal from "../Modal";
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import { ReactComponent as PlusIcon } from './assets/plus.svg';
import { format } from 'date-fns';
import { languageMapping } from '../../utils/languages';
import { toApiAffiliationTypes } from '../../utils/affiliationTypes';
import {toast} from "react-toastify";
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';

import styles from './AddToNarrative.module.scss';


export const AddToNarrativeModal = ({
  isOpen,
  areAllMessagesSelected,
  messages=null,
  onChange,
  isSearch=false,
  deduplication=false,
  caseId,
  searchQuery,
  messagesStatus,
  showOnlyFavorites,
  startDate,
  endDate,
  platforms,
  sourceGroups,
  languages,
  sources,
  countries,
  sentiment,
  contentTypes,
  showOnlyBots,
  discreditations,
  affiliationCountries,
  affiliationTypes,
  highRiskExclude,
  botsExclude,
  botsType,
  feeds,
  geoGroup,
  audienceRange,
  isVerified,
  suspendedByPlatform,
  immatureAccount,
  languagesExclude,
  countriesExclude,
  sourceGroupsExclude,
  sourcesExclude,
  affiliationCountriesExclude,
  affiliationTypesExclude,
  feedsExclude,
  geoGroupExclude,
  eventIds,
  opinionIds,
  widgetName,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState();
  const [narrativeSelect, setNarrativeSelect] = useState(false);

  
  const messagesList = messages ? Object.keys(messages) : null;
  const selectAllModalMessage = () => {
    toast.info(t('This process may take a few minutes. Please wait before checking the changes.'));
  }
  
  const userInstruction = <div className={styles.userNote}>
    {t(`Add publications to an existing case or create a new one by typing its name and pressing the “Create New Case” button.`)}
  </div>

  const getNarrativesOptions = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.set('size', '10');
    urlParams.set('page', 1);
    urlParams.set('is_threat', false);

    API.fetch('GET', `/API/v1/narratives?${urlParams.toString()}`).then(data => {
      let newOptions = data.objects.map(narrative => {return {label: narrative.name, value: narrative.id}});
      setOptions(newOptions)
    })
  }, [])

  useEffect(getNarrativesOptions, [getNarrativesOptions]);

  const getNarratives = (inputValue, callback) => {
    const urlParams = new URLSearchParams();
    urlParams.set('q', inputValue);
    urlParams.set('is_threat', false);

    return API.fetch('GET', `/API/v1/narratives?${urlParams.toString()}`).then((data) => {
      callback(
        data.objects
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((narrative) => {
            return {value: narrative.id, label: narrative.name};
          })
      );
    });
  };

  const handleCreate = (inputvalue) => {
    if(!deduplication) {
      setIsLoading(true);
      API.fetch('POST', `/API/v1/narratives`, null, {
        name: inputvalue,
        description: '',
        type: 'NARRATIVE',
        is_manual: true,
        parameters: null
      }).then(data => {
        let newOption = {value: data.id, label: data.name};
        setIsLoading(false);
        setOptions(prev => [...prev, newOption])
        setNarrative(newOption)
      })
      .catch(e => {
        // setError(e.message);
        setError(t('Something went wrong. Please, try again...'));
        setIsLoading(false);
      });
    } else {
      setIsLoading(true);
      let newOption = {value: 'new', label: inputvalue};
      setIsLoading(false);
      setOptions(prev => [...prev, newOption])
      setNarrative(newOption)
    } 
    
  }

  const handleAddToNarrative = () => {
    if(!messages && !areAllMessagesSelected) {
      setError(t('There are no selected publications'))
      return
    }

    const urlParams = new URLSearchParams();

    let body = {};
    if(isSearch) {
      body = {
        composite_message_ids: messagesList,
        status: "READ"
      }
    } else if(deduplication) {
      let newNarrative = null;
      if(narrative?.value === 'new') {
        let newCaseBody = {
          name: narrative?.label,
          description: '',
        }
        newNarrative = newCaseBody;
      } 
      body = {
        new_narrative: newNarrative,
        existing_narrative_id: narrative?.value === 'new' ? null : narrative?.value,
        story_ids: messagesList,
      }
    } else if (areAllMessagesSelected) {
      const keyword = searchQuery ? searchQuery.replace(/(\r\n|\n|\r)/gm, "") : "";

      if (keyword) {
          body['keywords'] = [keyword]
      }

      if (startDate) {
        body['start_date'] = format(startDate, 'yyyy-LL-dd')
      }

      if (endDate) {
          body['end_date'] = format(endDate, 'yyyy-LL-dd')
      }


      if (platforms && platforms.length > 0) {
        body.platforms = platforms;
      }

      if (sentiment && sentiment.length > 0) {
        body.sentiment = sentiment;
      }

      if (sourceGroups && sourceGroups.length > 0) {
        body.source_group_ids = sourceGroups;
      }

      if(languages && languages.length > 0) {
        body.languages = languages.map(lang => languageMapping[lang]);
      }

      if (contentTypes && contentTypes.length > 0) {
        body.content_types = contentTypes;
      }

      if(sources && sources.length > 0) {
        body.source_ids = sources;
      }

      if (countries && countries.length > 0) {
        body.source_origin_country_ids = countries;
      }

      if (showOnlyBots === true) {
        body.source_bots = showOnlyBots;
      }

      if (botsType) {
        if (botsType === 'inauthentic_behavior') {
          body.source_inauthentic_behavior = true;
        } else if (botsType === 'immature_account') {
          body.source_immature_account = true;
        } else if (botsType === 'suspended_by_platform') {
          body.source_suspended_by_platform = true;
        }
      } 

      if (discreditations && discreditations?.length > 0) {
        body.source_discreditated_entity_types = discreditations;
      }

      if(affiliationCountries?.length > 0 && affiliationCountries[0]?.value === 'all') {
        body.source_state_affiliated_all_countries = true;
      } else {
        body.source_state_affiliated_country_ids = affiliationCountries?.map(v => v.value);
      }

      if(affiliationTypes && affiliationTypes.length > 0) {
        body.source_affiliation_types = toApiAffiliationTypes(affiliationTypes);
      }

      if(feeds && feeds.length > 0) {
        body.feed_ids = feeds?.map(v => v.value);
      }

      if(geoGroup && geoGroup.length > 0) {
        body.country_group_ids = geoGroup?.map(v => v.value);
      }

      if (audienceRange) {
        body.source_audience_lte = audienceRange[1];
        body.source_audience_gte = audienceRange[0];
      }

      if (isVerified) {
        body.source_is_verified = isVerified;
      }

      if (suspendedByPlatform) {
        body.source_suspended_by_platform = suspendedByPlatform;
      }

      if (immatureAccount) {
        body.source_immature_account = immatureAccount;
      }

      let excludedFilters = [];

      if(languagesExclude) {
        excludedFilters.push('languages');
      }
      
      if(countriesExclude) {
        excludedFilters.push('source_origin_country_ids');
      }

      if(sourceGroupsExclude) {
        excludedFilters.push('source_group_ids');
      }

      if(sourcesExclude) {
        excludedFilters.push('source_ids');
      }

      if(affiliationCountriesExclude) {
        excludedFilters.push('source_state_affiliated_country_ids');
      }

      if(affiliationTypesExclude) {
        excludedFilters.push("source_affiliation_types");
      }

      if(highRiskExclude) {
        excludedFilters.push('source_discreditated_entity_types');
      }

      if(botsExclude) {
        excludedFilters.push('source_bots');
      }

      if(feedsExclude) {
        excludedFilters.push('feed_ids');
      }

      if(geoGroupExclude) {
        excludedFilters.push('country_group_ids');
      }

      if(excludedFilters.length > 0) {
        body.exclusive_filters = excludedFilters;
      }

      if (caseId) {
        urlParams.set('narrative_id', caseId);
      }

      if(messagesStatus) {
        messagesStatus.forEach(status => urlParams.append('message_status', status));
      }

      if(showOnlyFavorites) {
        urlParams.set('favorite', showOnlyFavorites);
      }

      if(eventIds) {
       body.topic_modeling_event_ids = eventIds;
      }

      if(opinionIds) {
        body.topic_modeling_opinion_ids = opinionIds;
      }

    } else {
      body = {
        message_ids: messagesList,
        status: "READ"
      }
    }

    let link = `/API/v1/narratives/${narrative?.value}/messages`;

    if (deduplication) {
      link = `/API/v1/narratives/${caseId}/copy_from_stories`
    }

    if(areAllMessagesSelected) {
      link = `/API/v1/narratives/${narrative?.value}/messages/from_search?${urlParams.toString()}`
    }

    API.fetch('POST', link, null, body).then((data) => {
      ampli.track({
        event_type: 'Save messages to case',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          widget_name: widgetName || '',
        }
      }); 
      if(areAllMessagesSelected) {
        selectAllModalMessage()
      }

      if(deduplication) {
        window.open(`/narratives/${data?.id}`, '_blank');
      } else {
        window.open(`/narratives/${narrative?.value}`, '_blank');
      }
      
    }).catch(e => {
      // setError(e.message);
      setError(t('Something went wrong. Please, try again...'));
    });
  }

  useEffect(() => {
    if(options) {
      const container = document.querySelector('#message-to-narrative-modal');
      if(narrativeSelect === true ) {
        let content = document.querySelector(".react-select__menu");
        if(container.offsetHeight < content.offsetHeight) {
          container.style.height=container.offsetHeight + content.offsetHeight + "px";
        }
      } else {
        container.style.height = ' ';
      }
    }
 }, [narrativeSelect])


   
  return (
      <Modal
        isVisible={isOpen}
        title={t('Add publications to case')}
        className={styles.addToNarrativeModal}
        doNotCloseOnOutsideClick={true}
        content={
          options ? <div id='message-to-narrative-modal' className={`${error ? 'form-element error' : 'form-element'}`}>
            {userInstruction}
            <label>{t('Case name')}</label>
            <AsyncCreatableSelect
              cacheOptions
              defaultOptions={options}
              value={narrative}
              loadOptions={getNarratives}
              isClearable
              isDisabled={isLoading}
              isLoading={isLoading}
              loadingMessage={() => t('Loading...')}
              placeholder={t('Start typing case name')}
              onChange={(newValue) => setNarrative(newValue)}
              onCreateOption={handleCreate}
              onMenuOpen={() => setNarrativeSelect(true)}
              onMenuClose={() => setNarrativeSelect(false)}
              menuIsOpen={narrativeSelect}
              className="react-select-container"
              classNamePrefix="react-select"
              formatCreateLabel={(inputValue) => <span className={styles.create}><PlusIcon/>{t('Create new case')} "{inputValue}"</span>}
              createOptionPosition='first'
            />
            {error ? <div className={styles.error}>{error}</div> : ''}
          </div> : <LoaderSmall/>
        }
        footer={
          <div className={styles.footer}>
            <button
              className="btn-primary"
              onClick={() => {
                handleAddToNarrative();
                setNarrative(null);
                setError(null);
                onChange(false);
              }}
              
            >
              {t('Save')}
            </button>
            <button
              className="btn-reset"
              onClick={() => {
                setNarrative(null);
                setError(null);
                onChange(false);
              }}
            >
              {t('Cancel')}
            </button>
          </div>
        }
        onClose={() => {
          onChange(false)
          setNarrative(null);
          setError(null);
        }
        }
      />
  )
}


